import type { LC, LiveElement, PropsWithChildren, ArrowFunction } from '@use-gpu/live';

import { memo, gather, unquote, useMemo, useRef, useResource, useState } from '@use-gpu/live';
import { useDeviceContext } from '../providers/device-provider';
import { QueueReconciler } from '../reconcilers/index';

const {reconcile, quote} = QueueReconciler;

export type DispatchLoopProps = PropsWithChildren<{
  /** Always run initial iteration immediately */
  initial?: boolean,
  /** Loop is running */
  live?: boolean,
  /** Continue iteration count even if program changes */
  continued?: boolean,
  /** Batch # of dispatches together */
  batch?: number,
  /** Limit # of dispatches */
  limit?: number,

  then?: (count: number) => LiveElement,
}>;

export const DispatchLoop: LC<DispatchLoopProps> = memo((props: DispatchLoopProps) => {
  const {
    initial = false,
    live = false,
    continued = false,
    batch = 1,
    limit,
    then,
    children,
  } = props;

  const device = useDeviceContext();

  const countRef = useRef(0);

  return (
    reconcile(
      quote(
        gather(
          unquote(children),
          (fs: ArrowFunction[]) => {
            const [tick, setTick] = useState(0);

            useMemo(() => {
              if (!continued) countRef.current = 0;
              // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [continued, ...fs]);

            useResource((dispose) => {
              let running = live;

              const run = () => {
                for (const f of fs) f();
              };

              const runBatch = () => {
                for (let i = 0; i < batch; ++i) run();
                countRef.current += batch;

                if (limit != null && countRef.current >= limit) {
                  running = false;
                }
              };

              if (running) {
                const loop = () => {
                  if (!running) return;

                  runBatch();
                  device.queue.onSubmittedWorkDone().then(() => {
                    setTick(countRef.current);
                    loop();
                  });
                };

                setTimeout(loop, 0);
              }
              else if (initial) {
                run();
              }

              dispose(() => running = false);
            }, [initial, live, batch, ...fs]);

            return then?.(tick);
          }
        )
      )
    )
  );
}, 'DispatchLoop');
